/**
 * ホスティングの画面の「URL スラッグ」のカード（2026-09-28 creator/slug-rename）。今のスラッグ・変える・元に戻す（undo）・古いスラッグの転送の ON / OFF。
 * 子: SlugChangeDialog.tsx（入力 → 確認 → 再認証 → 変更）・OldSlugsList.tsx（一覧と切り替えの確認）。判断は slugRename.ts（テストあり）。
 *
 * WHY ホスティングの画面に置くか: ゲームに「設定」の画面は無く、スラッグが効くのは公開 URL（production / development の両方）。
 *   公開 URL・独自ドメインと同じ画面にあれば「どこで遊べるか」が 1 か所で分かる。スラッグはプロジェクト全体の値なので、どちらの環境を
 *   見ていても同じカードを出す。MCP・CLI からの案内はこのカード（`/p/:id/production/hosting#slug`）へ向ける。
 * WHY 再認証を親に 1 つだけ持つか: 変更と転送の切り替えは同じ部品（useReauth）を使い、「Google で確認」から戻ったときの続き
 *   （action = slugRename.ts の slugChangeAction / slugRedirectAction）をここで 1 回だけ読んで、どちらを開き直すか決める。
 *   続きは自動では送らない（お金と URL が変わる操作 = 最後のボタンは本人が押す。押すと reauth.run が戻ってきた証明を使う）。
 * 再認証は共通の形（400 reauthentication_required / 401 reauthentication_failed）= 部品の run がそのまま扱う（Core の実装 2026-09-28）。
 * 古いサーバー（slug の口が無い）では GET が 404 route_missing = 「このサーバーにはまだ無い」を落ち着いて出す（errorText）。
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, type ApiError } from '../api';
import { useResource } from '../hooks';
import { errorText, formatDateTime, useT, type MessageKey } from '../i18n';
import {
  canChangeNow,
  isDailyProration,
  oldSlugPath,
  parseSlugAction,
  redirectBody,
  redirectMonthlyUsd,
  slugLeadMessage,
  slugPath,
  slugRedirectAction,
  undoAvailable,
  type SlugChangeResult,
  type SlugView
} from '../slugRename';
import { OldSlugsList, type RedirectToggle } from './OldSlugsList';
import { useReauth, useReauthFailureText } from './Reauth';
import { SlugChangeDialog, type SlugDialogInitial } from './SlugChangeDialog';
import { Spinner } from './ui';

export function SlugSettings({ projectId, onChanged }: { projectId: string; onChanged: () => void }) {
  const { t } = useT();
  const location = useLocation();
  const r = useResource<SlugView>(slugPath(projectId));
  const reauth = useReauth();
  const failureText = useReauthFailureText();
  const [dialog, setDialog] = useState<SlugDialogInitial | null>(null);
  const [toggle, setToggle] = useState<RedirectToggle | null>(null);
  const [busy, setBusy] = useState(false);
  const [toggleErr, setToggleErr] = useState<ApiError | null>(null);
  // 通知はキーと値だけ持ち、表示時に翻訳する（言語を切り替えても今の言語で出る。HostingDomains と同じ）
  const [notice, setNotice] = useState<Array<{ key: MessageKey; vars: Record<string, string> }> | null>(null);

  // 「Google で確認」から戻ったとき: 同じ内容の確認を開き直す（送るのは本人が押してから）
  const resumed = parseSlugAction(reauth.pending?.action);
  useEffect(() => {
    if (!resumed) return;
    if (resumed.kind === 'change') setDialog({ slug: resumed.slug, choice: resumed.choice, autoCheck: true });
    else setToggle({ oldSlug: resumed.oldSlug, on: resumed.on });
    // pending.action が変わったときだけ（resumed はその写し）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reauth.pending?.action]);
  const failure = reauth.failure && parseSlugAction(reauth.failure.action) ? reauth.failure : null;

  // MCP・CLI の案内の URL（…/hosting#slug）から来たら、このカードまで動かす（react-router は # へ動かさない）
  useEffect(() => {
    if (location.hash === '#slug' && r.data) document.getElementById('slug')?.scrollIntoView({ block: 'start' });
  }, [location.hash, r.data]);

  const changed = (res: SlugChangeResult) => {
    setDialog(null);
    setNotice([
      { key: 'slug.changed', vars: { slug: res.project.slug } },
      { key: res.redirectOldSlug ? 'slug.changedRedirect' : 'slug.changedNoRedirect', vars: { old: res.previousSlug } }
    ]);
    r.reload();
    // サイドバーのスラッグ・公開 URL（HostingPage）を読み直す
    onChanged();
  };

  const confirmToggle = async () => {
    if (!toggle || busy) return;
    setBusy(true);
    setToggleErr(null);
    try {
      const res = await reauth.run(
        (proof) => api<{ oldSlug: { slug: string; redirect: boolean } }>(oldSlugPath(projectId, toggle.oldSlug), { method: 'PATCH', body: redirectBody(toggle.on, proof) }),
        { action: slugRedirectAction(toggle.oldSlug, toggle.on) }
      );
      if (!res) return; // 本人の確認を取り消した = 確認のダイアログは開いたまま
      setNotice([{ key: res.oldSlug.redirect ? 'slug.old.updatedOn' : 'slug.old.updatedOff', vars: { old: res.oldSlug.slug } }]);
      setToggle(null);
      r.reload();
    } catch (e) {
      setToggleErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const view = r.data;
  const changeNow = canChangeNow(view);

  return (
    <section className="card slug-card" id="slug">
      <h2 className="card-title">{t('slug.title')}</h2>
      {r.loading && !view && <Spinner />}
      {r.error && (
        <p className={`alert ${r.error.notAvailable ? '' : 'alert-error'}`} role={r.error.notAvailable ? 'status' : 'alert'}>
          {errorText(r.error)}
        </p>
      )}
      {notice && (
        <div className="alert alert-ok" role="status">
          {notice.map((n) => (
            <p key={n.key}>{t(n.key, n.vars)}</p>
          ))}
        </div>
      )}
      {failure && (
        <p className="alert alert-error" role="alert">
          {failureText(failure)}
        </p>
      )}
      {view && (
        <>
          <p className="small muted">{(() => {
            const m = slugLeadMessage(view.changeIntervalDays, view.slug);
            return t(m.key, m.vars);
          })()}</p>
          <div className="slug-current">
            <strong className="mono slug-current-name">{view.slug}</strong>
            <span className="slug-current-actions">
              {undoAvailable(view) && view.undo && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => (setNotice(null), setDialog({ slug: view.undo!.slug, autoCheck: true }))}>
                  {t('slug.undo', { slug: view.undo.slug })}
                </button>
              )}
              <button type="button" className="btn btn-primary btn-sm" disabled={!changeNow} onClick={() => (setNotice(null), setDialog({}))}>
                {t('slug.change')}
              </button>
            </span>
          </div>
          {undoAvailable(view) && view.undo && <p className="small muted">{t('slug.undoNote', { date: formatDateTime(view.undo.until) })}</p>}
          {!changeNow && view.nextChangeAt && <p className="small muted">{t('slug.nextChangeAt', { date: formatDateTime(view.nextChangeAt) })}</p>}

          <OldSlugsList
            oldSlugs={view.oldSlugs}
            price={redirectMonthlyUsd(null, view)}
            daily={isDailyProration(null, view)}
            busy={busy}
            toggle={toggle}
            toggleError={toggleErr ? errorText(toggleErr) : null}
            onAsk={(next) => (setNotice(null), setToggleErr(null), setToggle(next))}
            onConfirm={() => void confirmToggle()}
            onCancel={() => {
              if (busy) return;
              setToggle(null);
              setToggleErr(null);
            }}
          />

          {dialog && <SlugChangeDialog projectId={projectId} view={view} initial={dialog} reauth={reauth} onDone={changed} onClose={() => !busy && setDialog(null)} />}
        </>
      )}
      {reauth.dialog}
    </section>
  );
}
